import { useEffect, useState, useMemo } from "react";
import PageLayout from "../../components/shared/PageLayout";
import ContactsList from "../../components/contacts/ContactsList";
import ContactsFilter from "../../components/contacts/ContactsFilter";
import ContactsSkeleton from "../../components/contacts/ContactsSkeleton";
import LoadMoreButton from "../../components/contacts/LoadMoreButton";
import FilterModal from "../../components/contacts/FilterModal";
import ExportFormatModal, {
  type ExportFormat,
} from "../../components/contacts/ExportFormatModal";
import {
  exportContactsAsCSV,
  exportContactsAsVCF,
  fetchTriggerDetails,
  fetchAllTriggersData,
} from "../../utils/apiService";
import { useContactsFilter } from "../../hooks/useContactsFilter";
import { useDashboard } from "../../hooks/useDashboard";
import type { Contact } from "../../types/contact";
import type { FilterState } from "../../components/contacts/ContactsFilter";
import type { TriggerData } from "../../types/dashboard";
import { StyledContacts } from "./index.styled";
import { colors } from "../../styles/colors";

// Source mapping function to match dashboard colors and names
const getSourceInfo = (source: string) => {
  const sourceMapping: { [key: string]: { label: string; color: string } } = {
    recommendation: { label: "קידום ממומן", color: colors.yellow },
    search: { label: "חיפוש חופשי", color: colors.red },
    suggestion: { label: "קידום", color: colors.orange },
    direct: { label: "טריגר ישיר", color: colors.teal },
    categories: { label: "קטגוריות", color: colors.purple },
  };

  return sourceMapping[source] || { label: source, color: colors.gray };
};

// Global cache for trigger details to persist across component re-renders
const globalTriggerCache = new Map<number, TriggerData>();
const globalLoadingTriggers = new Set<number>();

const Contacts = () => {
  const [currentFilterState, setCurrentFilterState] = useState<FilterState>({
    dateFilter: "all",
    sourceKey: undefined,
    startDate: "",
    endDate: "",
    selectedMonth: undefined,
    searchQuery: "",
  });
  const [isLoadingFromFilter, setIsLoadingFromFilter] =
    useState<boolean>(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [allTriggers, setAllTriggers] = useState<TriggerData[]>([]);
  const [triggersLoading, setTriggersLoading] = useState<boolean>(false);
  const [triggerDetails, setTriggerDetails] = useState<
    Map<number, TriggerData>
  >(new Map(globalTriggerCache));
  const [loadingTriggers, setLoadingTriggers] = useState<Set<number>>(
    new Set(globalLoadingTriggers)
  );
  const {
    status,
    contacts,
    contactsLoading,
    contactsPagination,
    refetchContactsData,
    refetchData,
  } = useDashboard();

  // Function to load all triggers
  const loadAllTriggers = async () => {
    if (triggersLoading) return;

    setTriggersLoading(true);
    try {
      console.log("Loading all triggers...");
      const response = await fetchAllTriggersData();
      console.log("Triggers response:", response);

      // Check if response is directly an array (like the data you showed)
      if (Array.isArray(response)) {
        console.log("Response is direct array, setting triggers:", response);
        setAllTriggers(response);
      } else if ("success" in response && response.success && Array.isArray(response.data)) {
        console.log(
          "Response has success/data structure, setting triggers:",
          response.data
        );
        setAllTriggers(response.data);
      } else {
        console.log("Invalid triggers response:", response);
      }
    } catch (error) {
      console.error("Error loading all triggers:", error);
    } finally {
      setTriggersLoading(false);
    }
  };

  // Function to load trigger details
  const loadTriggerDetails = async (triggerId: number) => {
    // Check global cache first
    if (
      globalTriggerCache.has(triggerId) ||
      globalLoadingTriggers.has(triggerId)
    ) {
      return; // Already loaded or loading globally
    }

    // Check local state as well
    if (triggerDetails.has(triggerId) || loadingTriggers.has(triggerId)) {
      return; // Already loaded or loading locally
    }

    // Add to both global and local loading sets
    globalLoadingTriggers.add(triggerId);
    setLoadingTriggers((prev) => new Set(prev).add(triggerId));

    try {
      const details = await fetchTriggerDetails(triggerId);

      // Update global cache
      globalTriggerCache.set(triggerId, details);

      // Update local state
      setTriggerDetails((prev) => {
        // Check again before setting to prevent overwriting
        if (!prev.has(triggerId)) {
          return new Map(prev).set(triggerId, details);
        }
        return prev;
      });
    } catch (error) {
      console.error(
        `Error loading trigger details for ID ${triggerId}:`,
        error
      );
    } finally {
      // Remove from both global and local loading sets
      globalLoadingTriggers.delete(triggerId);
      setLoadingTriggers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(triggerId);
        return newSet;
      });
    }
  };

  // Convert API data to Contact format
  const contactsData: Contact[] = contacts.map((contact, index) => {
    const sourceInfo = getSourceInfo(contact.source);
    const triggerId = contact.trigger_id;
    const triggerData = triggerId ? triggerDetails.get(triggerId) : undefined;

    // If we have a trigger_id but no trigger data yet, load it
    if (triggerId && !triggerData && !loadingTriggers.has(triggerId)) {
      loadTriggerDetails(triggerId);
    }

    // Use trigger name only if it exists and is not empty, otherwise check if it's direct trigger
    let displaySource;
    if (triggerData?.name && triggerData.name.trim() !== "") {
      // If we have trigger data with a valid name, use it
      displaySource = triggerData.name;
    } else if (sourceInfo.label === "טריגר ישיר" && !triggerId) {
      // If it's a direct trigger but no triggerId, show search result
      displaySource = "תוצאה ישירה מהחיפוש";
    } else {
      // Otherwise use original source label
      displaySource = sourceInfo.label;
    }

    return {
      id: contact.id || index, // Use index as fallback if id is missing
      name: contact.name,
      phone: contact.phone,
      joinDate: contact.join_date
        ? new Date(contact.join_date).toLocaleDateString("he-IL")
        : "",
      source: displaySource,
      sourceColor: sourceInfo.color,
      triggerId: triggerId,
      triggerName: triggerData?.name,
    };
  });

  const { filteredContacts } = useContactsFilter(
    contactsData,
    currentFilterState
  );

  // Load more contacts logic
  const loadMoreContacts = () => {
    if (
      status?.id &&
      contactsPagination?.has_next &&
      !contactsLoading &&
      !isLoadingFromFilter
    ) {
      const nextOffset =
        contactsPagination.offset + contactsPagination.per_page;

      // Check if sourceKey is a trigger
      let actualSourceKey = currentFilterState.sourceKey;
      let triggerId: number | undefined;

      if (
        currentFilterState.sourceKey &&
        currentFilterState.sourceKey.startsWith("trigger_")
      ) {
        const triggerIdStr = currentFilterState.sourceKey.replace(
          "trigger_",
          ""
        );
        triggerId = parseInt(triggerIdStr, 10);

        // For trigger_0 (direct search result), keep the source and pass trigger_id = 0
        if (triggerId === 0) {
          actualSourceKey = "direct"; // Keep the source as "direct" for direct search results
        } else {
          actualSourceKey = undefined; // Don't pass source when using other trigger_id
        }
      }

      console.log(
        "Loading more contacts, offset:",
        nextOffset,
        "with source filter:",
        actualSourceKey,
        "triggerId:",
        triggerId
      );
      refetchContactsData(
        status.id,
        currentFilterState.startDate || undefined,
        currentFilterState.endDate || undefined,
        50, // limit
        nextOffset,
        true, // append
        actualSourceKey || undefined, // include source filter (but not for triggers)
        currentFilterState.searchQuery || undefined, // include search filter
        triggerId // triggerId - properly handled here too
      );
    }
  };

  // Sources data based on subscription source mapping + triggers
  const sources = useMemo(() => {
    console.log("Building sources list, allTriggers:", allTriggers);
    const sourceMapping = [
      { key: "recommendation", label: "קידום ממומן", color: colors.yellow },
      { key: "search", label: "חיפוש חופשי", color: colors.red },
      { key: "suggestion", label: "קידום", color: colors.orange },
      { key: "direct", label: "טריגר ישיר", color: colors.teal },
      { key: "categories", label: "קטגוריות", color: colors.purple },
      {
        key: "trigger_0",
        label: "תוצאה ישירה מהחיפוש",
        color: colors.green,
        triggerId: 0,
      },
    ];

    // Add triggers as sources - only if they have a valid name
    const triggerSources = allTriggers
      .filter((trigger) => {
        // Only include triggers that have a valid name
        return trigger.name && trigger.name.trim() !== "";
      })
      .map((trigger) => ({
        key: `trigger_${trigger.ID}`,
        label: trigger.name,
        color: colors.blue, // Use blue color for triggers
        triggerId: trigger.ID,
      }));

    console.log("Trigger sources:", triggerSources);
    const finalSources = [...sourceMapping, ...triggerSources];
    console.log("Final sources:", finalSources);
    return finalSources;
  }, [allTriggers]);

  useEffect(() => {
    console.log("Contacts initial useEffect - status:", status?.id);

    // Set default date range to "until today"
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
    setCurrentFilterState((prev) => ({
      ...prev,
      endDate: today,
    }));

    // First try to load basic dashboard data to get status
    if (!status?.id) {
      console.log("No status ID, loading dashboard data first");
      refetchData();
    }

    // Load all triggers
    loadAllTriggers();
  }, []); // Empty dependency array to run only once on mount

  // Load contacts when status becomes available
  useEffect(() => {
    console.log(
      "Contacts useEffect - status:",
      status?.id,
      "contacts length:",
      contacts.length,
      "loading:",
      contactsLoading,
      "filterState:",
      currentFilterState,
      "isLoadingFromFilter:",
      isLoadingFromFilter
    );
    if (status?.id && !contactsLoading && !isLoadingFromFilter) {
      console.log(
        "Loading contacts for status ID:",
        status.id,
        "with filter state:",
        currentFilterState
      );

      // Only load if we have actual date values, not empty strings
      // Skip if both startDate and endDate are empty (for "today" filter)
      if (currentFilterState.startDate || currentFilterState.endDate) {
        // Check if sourceKey is a trigger
        let actualSourceKey = currentFilterState.sourceKey;
        let triggerId: number | undefined;

        if (
          currentFilterState.sourceKey &&
          currentFilterState.sourceKey.startsWith("trigger_")
        ) {
          const triggerIdStr = currentFilterState.sourceKey.replace(
            "trigger_",
            ""
          );
          triggerId = parseInt(triggerIdStr, 10);

          // For trigger_0 (direct search result), keep the source and pass trigger_id = 0
          if (triggerId === 0) {
            actualSourceKey = "direct"; // Keep the source as "direct" for direct search results
          } else {
            actualSourceKey = undefined; // Don't pass source when using other trigger_id
          }
        }

        refetchContactsData(
          status.id,
          currentFilterState.startDate || undefined,
          currentFilterState.endDate || undefined,
          50, // limit
          0, // offset
          false, // append
          actualSourceKey || undefined, // include source filter (but not for triggers)
          currentFilterState.searchQuery || undefined, // include search filter
          triggerId // triggerId - properly handled here too
        );
      }
    }
  }, [
    status?.id,
    currentFilterState.startDate,
    currentFilterState.endDate,
    isLoadingFromFilter,
    currentFilterState.sourceKey,
    currentFilterState.searchQuery,
  ]); // Add dependencies

  // Reset isLoadingFromFilter when contacts loading is complete
  useEffect(() => {
    if (!contactsLoading && isLoadingFromFilter) {
      console.log("Contacts loading completed, resetting isLoadingFromFilter");
      setIsLoadingFromFilter(false);
    }
  }, [contactsLoading, isLoadingFromFilter]);

  // Load trigger details for contacts that have trigger_id
  useEffect(() => {
    const contactsWithTriggers = contacts.filter(
      (contact) => contact.trigger_id
    );
    const triggerIds = contactsWithTriggers.map(
      (contact) => contact.trigger_id!
    );

    // Load trigger details for any new trigger IDs
    triggerIds.forEach((triggerId) => {
      // Check both global cache and local state
      if (
        !globalTriggerCache.has(triggerId) &&
        !globalLoadingTriggers.has(triggerId) &&
        !triggerDetails.has(triggerId) &&
        !loadingTriggers.has(triggerId)
      ) {
        loadTriggerDetails(triggerId);
      }
    });
  }, [contacts]); // Removed triggerDetails and loadingTriggers from dependencies to prevent infinite loops

  // Debug useEffect to track allTriggers changes
  useEffect(() => {
    console.log("allTriggers state changed:", allTriggers);
  }, [allTriggers]);

  const handleFilterModalApply = (filterState: FilterState) => {
    handleFilterChange(filterState);
    setIsFilterModalOpen(false);
  };

  const handleFilterModalClose = () => {
    setIsFilterModalOpen(false);
  };

  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterChange = (filterState: FilterState) => {
    console.log("Filter changed to:", filterState);
    setCurrentFilterState(filterState);
    setIsLoadingFromFilter(true);

    if (status?.id) {
      const { dateFilter, sourceKey, startDate, endDate, selectedMonth } =
        filterState;

      let actualSourceKey = sourceKey;
      let triggerId: number | undefined;

      if (sourceKey && sourceKey.startsWith("trigger_")) {
        const triggerIdStr = sourceKey.replace("trigger_", "");
        triggerId = parseInt(triggerIdStr, 10);

        // For trigger_0 (direct search result), keep the source and pass trigger_id = 0
        if (triggerId === 0) {
          actualSourceKey = "direct"; // Keep the source as "direct" for direct search results
        } else {
          actualSourceKey = undefined; // Don't pass source when using other trigger_id
        }
      }

      if (dateFilter === "all") {
        // Load all contacts until today, with optional source filter
        const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
        console.log(
          "Loading all contacts until today:",
          today,
          "with source:",
          actualSourceKey,
          "triggerId:",
          triggerId
        );
        // Update the filter state to include the end date for "load more" functionality
        setCurrentFilterState((prev) => ({
          ...prev,
          endDate: today,
        }));
        refetchContactsData(
          status.id,
          undefined,
          today,
          50,
          0,
          false,
          actualSourceKey,
          filterState.searchQuery || undefined,
          triggerId
        );
      } else if (dateFilter === "today") {
        // Load contacts for today using last=today, with optional source filter
        console.log(
          "Loading contacts for today using last=today, with source:",
          actualSourceKey,
          "triggerId:",
          triggerId
        );
        refetchContactsData(
          status.id,
          "today",
          undefined,
          50,
          0,
          false,
          actualSourceKey,
          filterState.searchQuery || undefined,
          triggerId
        );
      } else if (dateFilter === "this_week") {
        // Load contacts for this week using last=this_week, with optional source filter
        console.log(
          "Loading contacts for this week using last=this_week, with source:",
          actualSourceKey,
          "triggerId:",
          triggerId
        );
        refetchContactsData(
          status.id,
          "this_week",
          undefined,
          50,
          0,
          false,
          actualSourceKey,
          filterState.searchQuery || undefined,
          triggerId
        );
      } else if (dateFilter === "month" && selectedMonth) {
        // Load contacts for a specific month using explicit start/end dates, with optional source filter
        const year = new Date().getFullYear();
        const monthPadded = String(selectedMonth).padStart(2, "0");
        const monthStart = `${year}-${monthPadded}-01`;
        const lastDay = new Date(year, selectedMonth, 0).getDate();
        const monthEnd = `${year}-${monthPadded}-${String(lastDay).padStart(
          2,
          "0"
        )}`;
        console.log(
          "Loading contacts for month:",
          selectedMonth,
          "with dates:",
          monthStart,
          "to",
          monthEnd,
          "with source:",
          actualSourceKey,
          "triggerId:",
          triggerId
        );
        refetchContactsData(
          status.id,
          monthStart,
          monthEnd,
          50,
          0,
          false,
          actualSourceKey,
          filterState.searchQuery || undefined,
          triggerId
        );
      } else if (dateFilter === "dateRange" && (startDate || endDate)) {
        // Load contacts with date range filter, with optional source filter
        const effectiveStartDate = startDate || endDate;
        const effectiveEndDate =
          endDate || startDate || new Date().toISOString().split("T")[0];

        console.log(
          "Loading contacts with date range:",
          effectiveStartDate,
          "to",
          effectiveEndDate,
          "with source:",
          actualSourceKey,
          "triggerId:",
          triggerId
        );
        refetchContactsData(
          status.id,
          effectiveStartDate,
          effectiveEndDate,
          50,
          0,
          false,
          actualSourceKey,
          filterState.searchQuery || undefined,
          triggerId
        );
      } else if (sourceKey && !startDate && !endDate) {
        // Load contacts filtered by source until today (like "all" filter)
        const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
        console.log(
          "Loading contacts filtered by source:",
          actualSourceKey,
          "triggerId:",
          triggerId,
          "until today:",
          today
        );
        refetchContactsData(
          status.id,
          undefined,
          today,
          50,
          0,
          false,
          actualSourceKey,
          filterState.searchQuery || undefined,
          triggerId
        );
      }
    }
  };

  const handleExportClick = () => {
    setIsExportModalOpen(true);
  };

  const handleExport = async (format: ExportFormat) => {
    if (!status?.id) {
      console.error("No status ID available for export");
      return;
    }

    setIsExporting(true);
    setIsExportModalOpen(false);

    try {
      console.log(
        "Exporting contacts with format:",
        format,
        "and filter state:",
        currentFilterState
      );

      // Prepare parameters based on current filter state (without pagination)
      let fromDate: string | undefined;
      let toDate: string | undefined;
      const sourceKey = currentFilterState.sourceKey;

      // Handle different date filter types
      if (currentFilterState.dateFilter === "today") {
        fromDate = "today";
      } else if (currentFilterState.dateFilter === "this_week") {
        fromDate = "this_week";
      } else if (
        currentFilterState.dateFilter === "month" &&
        currentFilterState.selectedMonth
      ) {
        // Convert month to date range
        const year = new Date().getFullYear();
        const monthPadded = String(currentFilterState.selectedMonth).padStart(
          2,
          "0"
        );
        fromDate = `${year}-${monthPadded}-01`;
        const lastDay = new Date(
          year,
          currentFilterState.selectedMonth,
          0
        ).getDate();
        toDate = `${year}-${monthPadded}-${String(lastDay).padStart(2, "0")}`;
      } else if (currentFilterState.dateFilter === "dateRange") {
        fromDate = currentFilterState.startDate || undefined;
        toDate = currentFilterState.endDate || undefined;
      } else if (currentFilterState.dateFilter === "all") {
        // For "all" filter, use until today
        fromDate = undefined;
        toDate = new Date().toISOString().split("T")[0];
      }

      // Check if sourceKey is a trigger
      let actualSourceKey = sourceKey;
      let triggerId: number | undefined;

      if (sourceKey && sourceKey.startsWith("trigger_")) {
        const triggerIdStr = sourceKey.replace("trigger_", "");
        triggerId = parseInt(triggerIdStr, 10);

        // For trigger_0 (direct search result), keep the source and pass trigger_id = 0
        if (triggerId === 0) {
          actualSourceKey = "direct"; // Keep the source as "direct" for direct search results
        } else {
          actualSourceKey = undefined; // Don't pass source when using other trigger_id
        }
      }

      // Call the appropriate export function
      if (format === "csv") {
        await exportContactsAsCSV(
          fromDate,
          toDate,
          actualSourceKey,
          currentFilterState.searchQuery,
          triggerId
        );
      } else {
        await exportContactsAsVCF(
          fromDate,
          toDate,
          actualSourceKey,
          currentFilterState.searchQuery,
          triggerId
        );
      }

      console.log(`Export ${format.toUpperCase()} completed successfully`);
    } catch (error) {
      console.error(
        `Error exporting contacts as ${format.toUpperCase()}:`,
        error
      );
    } finally {
      setIsExporting(false);
    }
  };

  // Show skeleton while loading or when we don't have status yet
  // Only show skeleton if this is initial loading (no contacts yet)
  if ((contactsLoading && contacts.length === 0) || !status?.id) {
    return (
      <PageLayout
        title="אנשי קשר"
        StyledComponent={StyledContacts}
        wrapInContainer={false}
      >
        <ContactsSkeleton count={8} />
      </PageLayout>
    );
  }

  // Show "no contacts found" if total is 0 or no contacts at all
  // But don't show it if we're currently loading from a filter change
  if (
    contactsPagination?.total === 0 ||
    (contacts.length === 0 && !contactsLoading && !isLoadingFromFilter)
  ) {
    return (
      <PageLayout
        title="אנשי קשר"
        StyledComponent={StyledContacts}
        wrapInContainer={false}
      >
        <ContactsList contacts={[]} />
        <ContactsFilter
          onFilterChange={handleFilterChange}
          onOpenFilterModal={handleOpenFilterModal}
          sources={sources}
          filteredCount={0}
          totalCount={0}
          onExport={handleExportClick}
          exportDisabled={true}
          isExporting={isExporting}
          selectedMonth={currentFilterState.selectedMonth}
          activeFilter={currentFilterState.dateFilter}
          selectedSourceKey={currentFilterState.sourceKey}
          startDate={currentFilterState.startDate}
          endDate={currentFilterState.endDate}
          searchQuery={currentFilterState.searchQuery}
        />

        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={handleFilterModalClose}
          onApply={handleFilterModalApply}
          currentFilterState={currentFilterState}
          sources={sources}
        />

        <ExportFormatModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          onFormatSelect={handleExport}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="אנשי קשר"
      StyledComponent={StyledContacts}
      wrapInContainer={false}
    >
      <ContactsList contacts={filteredContacts} />

      <LoadMoreButton
        onClick={loadMoreContacts}
        loading={contactsLoading}
        hasMore={contactsPagination?.has_next || false}
        currentCount={contacts.length}
        totalCount={contactsPagination?.total}
      />

      <ContactsFilter
        onFilterChange={handleFilterChange}
        onOpenFilterModal={handleOpenFilterModal}
        sources={sources}
        filteredCount={filteredContacts.length}
        totalCount={contactsPagination?.total}
        onExport={handleExportClick}
        exportDisabled={filteredContacts.length === 0 || isExporting}
        isExporting={isExporting}
        selectedMonth={currentFilterState.selectedMonth}
        activeFilter={currentFilterState.dateFilter}
        selectedSourceKey={currentFilterState.sourceKey}
        startDate={currentFilterState.startDate}
        endDate={currentFilterState.endDate}
        searchQuery={currentFilterState.searchQuery}
      />

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={handleFilterModalClose}
        onApply={handleFilterModalApply}
        currentFilterState={currentFilterState}
        sources={sources}
      />

      <ExportFormatModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onFormatSelect={handleExport}
      />
    </PageLayout>
  );
};

export default Contacts;
